// hooks/useCreateApp.js
import { useReducer, useCallback } from 'react';

// Action Types
const BOT_CONTENT_ACTION_TYPES = {
  SET_BOT_CONTENT: 'SET_BOT_CONTENT',
  SET_LOADING: 'SET_LOADING',
  SET_UPDATING: 'SET_UPDATING',
  SET_ALERT: 'SET_ALERT',
  RESET_BOT_CONTENT: 'RESET_BOT_CONTENT'
};

// Default theme settings
const default_chat_settings = {
  redirect_uri: "/",
  theme: {
    theme_mode: "light",
    chat_opener_theme: {
      textColor: "#ffffff",
      backgroundColor: "#a370ce",
    },
    header_theme: {
      textColor: "#ffffff",
      backgroundColor: "#d00135",
    },
    chat_box_theme: {
      textColor: "#ffffff",
      backgroundColor: "#008000",
    },
  },
};

// Default bot content structure
const defaultBotContent = {
  app_name: "",
  WELCOME_MSG: "", // Keep for backward compatibility
  kb_id: "",
  OFFLINE_MSG: "", // Keep for backward compatibility
  FAQ: null,
  ai_agent_bot_enabled: false,
  dont_show_chat_box_at_logout: false,
  exclude_paths: [],
  live_agent_enabled: false,
  session_enabled: false,
  
  // ADD: live_settings structure
  live_settings: {
    WELCOME_MSG: "",
    OFFLINE_MSG: ""
  },

  allow_nested: false,
  app_id: "",
  auth_key: "",
  chat_settings: default_chat_settings,
  enabled_bot_content: false,
  ingestion_methods: {
    files: [],
    sitemap: null,
    urls: []
  },
  region: "US",
  role: "Owner",
  settings: {
    version: "V4",
    app_type: "P2A"
  },
  tenant_id: "",
  timestamp: 1763598287,
  tenant_info: {
    image: "",
    id: "",
    full_name: ""
  }
};

// Reducer
const botContentReducer = (state, action) => {
  switch (action.type) {
    case BOT_CONTENT_ACTION_TYPES.SET_BOT_CONTENT:
      return {
        ...state,
        botContent: typeof action.payload === 'function'
          ? action.payload(state.botContent)
          : action.payload
      };

    case BOT_CONTENT_ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    case BOT_CONTENT_ACTION_TYPES.SET_UPDATING:
      return {
        ...state,
        updating: action.payload
      };

    case BOT_CONTENT_ACTION_TYPES.SET_ALERT:
      return {
        ...state,
        alert: action.payload
      };

    case BOT_CONTENT_ACTION_TYPES.RESET_BOT_CONTENT:
      return {
        ...state,
        botContent: { ...defaultBotContent },
        updating: false,
        alert: {
          show: false,
          type: "info",
          message: "",
        }
      };

    default:
      return state;
  }
};

const initialState = {
  botContent: { ...defaultBotContent },
  loading: false,
  updating: false,
  alert: {
    show: false,
    type: "info",
    message: "",
  },
};

export const useCreateApp = () => {
  const [state, dispatch] = useReducer(botContentReducer, initialState);

  // Action creators
  const setBotContent = useCallback((botContent) => {
    dispatch({ type: BOT_CONTENT_ACTION_TYPES.SET_BOT_CONTENT, payload: botContent });
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({ type: BOT_CONTENT_ACTION_TYPES.SET_LOADING, payload: loading });
  }, []);

  const setUpdating = useCallback((updating) => {
    dispatch({ type: BOT_CONTENT_ACTION_TYPES.SET_UPDATING, payload: updating });
  }, []);

  const setAlert = useCallback((alert) => {
    dispatch({ type: BOT_CONTENT_ACTION_TYPES.SET_ALERT, payload: alert });
  }, []);

  const resetBotContent = useCallback(() => {
    dispatch({ type: BOT_CONTENT_ACTION_TYPES.RESET_BOT_CONTENT });
  }, []);

  return {
    // State
    botContent: state.botContent,
    loading: state.loading,
    updating: state.updating,
    alert: state.alert,

    // Actions
    setBotContent,
    setLoading,
    setUpdating,
    setAlert,
    resetBotContent
  };
};